import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useFormik } from "formik";
import {
  CardNumberInput,
  CardExpiryInput,
  CardCvcInput,
} from "./StripeElementsWrapper";

import AddressInputForm from "../forms/textField/AddressInputForm";
import { addressInputInterface } from "../../interfaces/forms/addressInput.interface";
import { getFromLocalStorage } from "../../config/localStorage";
import { validateForm } from "../../utils/inputValidator";
import { addressValidationSchema } from "../../utils/validationSchemas";

interface StripeCardCheckoutProps {
  setComponentToDisplay: (selection: string) => void;
  setPaymentMethod: (paymentMethod: Record<string, any | undefined>) => void;
  isBillingSame: boolean;
  setIsBillingSame: (isSame: boolean) => void;
  // billingData: addressInputInterface;
  // setBillingData: React.Dispatch<React.SetStateAction<addressInputInterface>>;
}

const StripeCardCheckout: React.FC<StripeCardCheckoutProps> = ({
  setComponentToDisplay,
  setPaymentMethod,
  isBillingSame,
  setIsBillingSame,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
      country: "",
    },
    validationSchema: addressValidationSchema,
    onSubmit: async (values) => {
      setIsProcessing(true);

      if (!stripe || !elements) return;

      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) return;

      const billingDetails = {
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        phone: values.phoneNumber,
        address: {
          line1: values.address1,
          line2: values.address2,
          city: values.city,
          state: values.state,
          postal_code: values.zipcode,
          country: values.country,
        },
      };

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: billingDetails,
      });

      if (error) {
        setErrorMessage(error.message || "Payment failed");
        setIsProcessing(false);
        return;
      }

      setPaymentMethod(paymentMethod);

      setIsProcessing(false);
      setComponentToDisplay("ORDERSUMMARY");
    },
  });

  useEffect(() => {
    if (isBillingSame) {
      const customerData = getFromLocalStorage("customerInfo");
      formik.setValues(customerData);
    }
  }, [isBillingSame]);

  const textFieldStyles = {
    "& .MuiInputLabel-root": {
      color: theme.palette.primary.dark,
    },
    "& .MuiInputBase-input": {
      color: theme.palette.primary.dark,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.dark,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.dark,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.dark,
      },
    },
  };

  return (
    <Container>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => setComponentToDisplay("CUSTOMERINFORMATION")}
          sx={{ color: theme.palette.primary.dark }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: 18, textAlign: "center", flexGrow: 1 }}>
          Payment Information
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Card Number"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputComponent: CardNumberInput,
                // inputProps: { options: cardElementOptions },
              }}
              variant="outlined"
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Expiry Date"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputComponent: CardExpiryInput,
                // inputProps: { options: cardElementOptions },
              }}
              variant="outlined"
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="CVV"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputComponent: CardCvcInput,
                // inputProps: { options: cardElementOptions },
              }}
              variant="outlined"
              sx={textFieldStyles}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Billing address same as customer address?
            </FormLabel>
            <RadioGroup
              row
              aria-label="billingAddress"
              name="billingAddress"
              value={isBillingSame ? "yes" : "no"}
              onChange={(e) => setIsBillingSame(e.target.value === "yes")}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>
        {!isBillingSame && (
          <AddressInputForm
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            touched={formik.touched}
            textFieldStyles={textFieldStyles}
          />
        )}
        {errorMessage && (
          <Grid item xs={12}>
            <Typography color="error">{errorMessage}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2, bgcolor: theme.palette.primary.main }}
            type="submit"
            disabled={!stripe || isProcessing}
          >
            {isProcessing ? "Processing..." : "Review"}
          </Button>
        </Grid>
      </form>
    </Container>
  );
};

export default StripeCardCheckout;
